import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import api from "@api/api.js";
import t from "@/services/translationService.js";
import UserEditModal from "@modules/user/UserEditModal.js";
import { editButton } from "./rowActions.js";

/**
 * Cockpit → User Manager. List users, add/edit/delete. Replaces the former
 * standalone #/users route.
 *
 * Deleting is inside the modal, not on the row — every Cockpit list works that
 * way, so nothing destructive is one stray click away in a list you were only
 * scrolling through.
 *
 * props: { lang }
 */
export default class UserManagerPanel extends CockpitPanel {
  async onBeforeMount() {
    Object.assign(this.state, await this.fetchData());
  }

  async fetchData() {
    try {
      const res = await api.get("/api/users");
      return { users: res.users || [], error: "" };
    } catch (err) {
      return { users: [], error: err.message || t.t("cockpit.users.loadError") };
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.users"));
  }

  lead() {
    return Component.escape(t.t("cockpit.users.lead"));
  }

  actions() {
    return `<button type="button" class="btn-primary" data-action="add">+ ${Component.escape(t.t("cockpit.users.add"))}</button>`;
  }

  /**
   * Read-only. Each account's second factor is managed from that account's own
   * Cockpit → My Account; nobody can enrol or disable it on someone else's
   * behalf. Shown here because an admin has total access, so a single
   * unprotected admin account undoes everyone else's second factor — and
   * without this there was no way to notice.
   */
  #twoFactorCell(user) {
    if (user.totpEnabled) {
      return `<span class="state-badge state-badge--public">${Component.escape(t.t("cockpit.users.twoFactorOn"))}</span>`;
    }
    // Only flagged as a risk for admins; a non-admin without 2FA is ordinary.
    const key = user.admin ? "public" : "private";
    const label = user.admin ? t.t("cockpit.users.twoFactorOffAdmin") : t.t("cockpit.users.twoFactorOff");
    return `<span class="state-badge state-badge--${user.admin ? "draft" : key}">${Component.escape(label)}</span>`;
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);

    const users = this.state.users || [];
    if (!users.length) return CockpitPanel.empty(t.t("cockpit.users.empty"));

    const rows = users
      .map(
        (user) => `
        <tr>
          <td><span class="cockpit-row__name">${Component.escape(user.name)}</span></td>
          <td>${Component.escape(user.email)}</td>
          <td><span class="role-badge role-badge--${user.admin ? "admin" : "user"}">${Component.escape(user.admin ? t.t("cockpit.users.roleAdmin") : t.t("cockpit.users.roleUser"))}</span></td>
          <td>${this.#twoFactorCell(user)}</td>
          <td class="cockpit-table__actions">${editButton(user.userId)}</td>
        </tr>`
      )
      .join("");

    return `
      <div class="cockpit-table__wrap">
        <table class="cockpit-table">
          <thead>
            <tr>
              <th>${Component.escape(t.t("cockpit.users.name"))}</th>
              <th>${Component.escape(t.t("cockpit.users.email"))}</th>
              <th>${Component.escape(t.t("cockpit.users.role"))}</th>
              <th>${Component.escape(t.t("cockpit.users.twoFactor"))}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  onMounted() {
    const add = this.$('[data-action="add"]');
    if (add) this.listen(add, "click", () => this.#openEditor(null));

    this.$$("[data-edit]").forEach((btn) =>
      this.listen(btn, "click", () =>
        this.#openEditor(this.state.users.find((u) => u.userId === btn.getAttribute("data-edit")))
      )
    );
  }

  #openEditor(user) {
    new UserEditModal({ user: user || null, onSaved: () => this.reload() }).mount(document.body);
  }

  async reload() {
    this.setState(await this.fetchData());
  }
}
